import { GsFormFieldColor, GsFormSchema } from 'libs/shared/src/components';
import { SharedItemOverlayDisplayMode, GsStructTextAlignment } from 'libs/shared/src/models';
import { ACTIVE_SCHEMA_FIELDS } from './../basics/active.schema';
import { BASIC_SCHEMA_FIELDS } from './../basics/basic.schema';
import { borderSchemaFields } from './../basics/border.schema';
import { PICTURE_DISPLAY_MODE_WITH_PHOTOS_SCHEMA } from './../basics/picture-display-mode.schema';
import { Mode, SWIPER_WITH_GRID_SCHEMA_FIELDS } from './../basics/swiper.schema';
import { teaserBadgeSchemaFields } from './../basics/teaser-badge.schema';
import { SPACING_SCHEMA } from './spacing.schema';

/**
 * The schema for the search-gallery component form.
 * Defines the fields to display for the form.
 */

export const SEARCH_GALLERY_COMPONENT_FORM_SCHEMA: GsFormSchema = {
  fields: [
    ...ACTIVE_SCHEMA_FIELDS,
    ...BASIC_SCHEMA_FIELDS,
    ...SPACING_SCHEMA,
    {
      formControlName: 'mode',
      label: 'schema.displayMode.name',
      type: 'select',
      valueType: 'number',
      default: Mode.GRID,
      options: [
        { text: 'schema.displayMode.grid', value: Mode.GRID },
        { text: 'schema.displayMode.slider', value: Mode.SLIDER }
      ]
    },
    ...teaserBadgeSchemaFields(),
    {
      formControlName: 'showDateOnImage',
      label: 'components.searchGallery.schema.showDateOnImage',
      type: 'checkbox',
      default: true,
      groupingName: 'components.searchGallery.schema.itemTeaserOptions'
    },
    ...SWIPER_WITH_GRID_SCHEMA_FIELDS,
    {
      formControlName: 'newForDays',
      label: 'components.searchGallery.schema.newForDays',
      type: 'number',
      default: 10,
      min: 1
    },
    {
      formControlName: 'itemTextAlignment',
      label: 'schema.alignment.text.name',
      type: 'select',
      valueType: 'number',
      default: GsStructTextAlignment.left,
      groupingName: 'components.searchGallery.schema.text.name',
      options: [
        { text: 'schema.alignment.text.left', value: GsStructTextAlignment.left },
        { text: 'schema.alignment.text.center', value: GsStructTextAlignment.center },
        { text: 'schema.alignment.text.right', value: GsStructTextAlignment.right },
        { text: 'schema.alignment.text.justify', value: GsStructTextAlignment.justify }
      ]
    },
    {
      formControlName: 'showItemSubtitle',
      label: 'components.searchGallery.schema.text.showItemSubtitle.label',
      type: 'checkbox',
      groupingName: 'components.searchGallery.schema.text.name',
      description: 'components.searchGallery.schema.text.showItemSubtitle.description',
      default: true
    },
    {
      formControlName: 'shortenItemTitles',
      label: 'components.searchGallery.schema.text.shortenItemTitles.label',
      type: 'checkbox',
      groupingName: 'components.searchGallery.schema.text.name',
      description: 'components.searchGallery.schema.text.shortenItemTitles.description',
      default: false
    },
    {
      formControlName: 'showPhotos',
      label: 'components.searchGallery.schema.showPhotos',
      type: 'checkbox',
      groupingName: 'schema.items.name',
      default: true
    },
    ...borderSchemaFields('ItemImage', undefined, 'schema.border.name'),
    {
      formControlName: 'forceSquareImage',
      label: 'schema.items.forceSquareImage',
      type: 'checkbox',
      default: false,
      if: {
        args: [{ formControlName: 'showPhotos', value: true }]
      }
    },
    {
      formControlName: 'itemImageHeight',
      label: 'schema.items.itemImageHeight',
      type: 'number',
      isResponsive: true,
      default: { default: 200 },
      if: {
        args: [
          { formControlName: 'showPhotos', value: true },
          { formControlName: 'forceSquareImage', value: false }
        ]
      }
    },
    {
      formControlName: 'showWatchlistButton',
      label: 'components.searchGallery.shared.showWatchlistButton',
      type: 'checkbox',
      default: false
    },
    {
      formControlName: 'showShoppingCartButton',
      label: 'components.searchGallery.shared.showShoppingCartButton',
      type: 'checkbox',
      default: false
    },
    {
      formControlName: 'showDateInTitle',
      label: 'components.searchGallery.schema.showDateInTitle',
      type: 'checkbox',
      default: true,
      groupingName: 'components.searchGallery.schema.showDate'
    },
    {
      formControlName: 'hoverLinkHint',
      label: 'components.searchGallery.shared.itemHoverLinkHint',
      type: 'label',
      nullable: true,
      readonly: true,
      groupingName: 'schema.hover.name'
    },
    {
      formControlName: 'itemZoomOnHover',
      label: 'components.image.schema.zoomOnHover',
      type: 'checkbox',
      default: false,
      groupingName: 'schema.hover.name'
    },
    {
      formControlName: 'itemHoverText',
      label: 'components.searchGallery.shared.itemHoverText',
      type: 'text',
      default: '',
      groupingName: 'schema.hover.name'
    },
    {
      formControlName: 'itemHoverTextColor',
      label: 'components.searchGallery.shared.itemHoverTextColor',
      type: 'color',
      default: 'secondary-main',
      groupingName: 'schema.hover.name',
      if: {
        args: [{ formControlName: 'itemHoverText', value: '', operator: '!=' }]
      }
    } as GsFormFieldColor,
    {
      formControlName: 'itemShowHoverBackground',
      label: 'components.searchGallery.shared.itemShowHoverBackground',
      type: 'checkbox',
      default: false,
      groupingName: 'schema.hover.name'
    },
    {
      formControlName: 'itemHoverBackgroundColor',
      label: 'components.searchGallery.shared.itemHoverBackgroundColor',
      type: 'color',
      default: 'secondary-main',
      groupingName: 'schema.hover.name',
      if: {
        args: [{ formControlName: 'itemShowHoverBackground', value: true }]
      }
    },
    {
      formControlName: 'showItemButton',
      label: 'components.searchGallery.schema.showItemButton',
      type: 'checkbox',
      default: false,
      groupingName: 'components.searchGallery.schema.itemButton'
    },
    {
      formControlName: 'itemButtonTextByArticleGroup',
      label: 'schema.items.itemButtonTextByArticleGroup.label',
      description: 'schema.items.itemButtonTextByArticleGroup.description',
      type: 'checkbox',
      default: false,
      groupingName: 'components.searchGallery.schema.itemButton',
      if: {
        args: [{ formControlName: 'showItemButton', value: true }]
      }
    },
    {
      formControlName: 'itemButtonText',
      label: 'components.searchGallery.schema.itemButtonText',
      type: 'text',
      default: '',
      placeholder: 'components.searchGallery.schema.itemButtonTextPlaceholder',
      groupingName: 'components.searchGallery.schema.itemButton',
      if: {
        args: [{ formControlName: 'showItemButton', value: true }]
      }
    },
    {
      formControlName: 'itemOverlayDisplayMode',
      label: 'components.searchGallery.schema.itemOverlayDisplayMode.label',
      description: 'components.searchGallery.schema.itemOverlayDisplayMode.description',
      type: 'select',
      valueType: 'number',
      default: SharedItemOverlayDisplayMode.onlyWithText,
      options: [
        {
          text: 'components.searchGallery.schema.itemOverlayDisplayMode.options.never',
          value: SharedItemOverlayDisplayMode.never
        },
        {
          text: 'components.searchGallery.schema.itemOverlayDisplayMode.options.onlyWithText',
          value: SharedItemOverlayDisplayMode.onlyWithText
        },
        {
          text: 'components.searchGallery.schema.itemOverlayDisplayMode.options.always',
          value: SharedItemOverlayDisplayMode.always
        }
      ]
    },
    ...PICTURE_DISPLAY_MODE_WITH_PHOTOS_SCHEMA
  ] as any
};
